/**
 * Reason Templates for Pending and Clarification Statuses
 * 
 * Industry-standard workflow improvement for title production systems.
 * Templates support dynamic placeholders that get replaced with jurisdiction data.
 * 
 * Supported placeholders:
 * - {jurisdiction_name} - Name of the jurisdiction (e.g., "New Castle County")
 * - {tax_office_name} - Tax office name
 * - {primary_phone} - Tax collector phone number
 * - {phone_number} - Tax collector phone number (alias)
 * - {fax} - Fax number
 * - {email} - Email address
 * - {website} - Website URL
 * - {parcel_apn} - Parcel/APN number
 * - {owner_name} - Property owner name
 * - {tax_year} - Tax year
 * - {due_date} - Due date
 * - {delinquent_date} - Delinquent date
 * - {county} - County name
 */

export const PENDING_REASON_TEMPLATES = [
  {
    id: 'no_response',
    label: 'No Response from Tax Office',
    template: 'Tried reaching the {jurisdiction_name} tax office{phone_suffix}, but no response was received. We will follow up again.',
    requiresJurisdiction: true,
  },
  {
    id: 'awaiting_callback',
    label: 'Awaiting Tax Office Callback',
    template: 'Left a message with the {jurisdiction_name} tax office{phone_suffix}. Awaiting callback.',
    requiresJurisdiction: true,
  },
  {
    id: 'awaiting_tax_cycle',
    label: 'Awaiting Updated Tax Cycle',
    template: 'Waiting for {jurisdiction_name} to update their tax records for the new tax cycle. Expected update within 2-3 business days.',
    requiresJurisdiction: true,
  },
  {
    id: 'portal_unavailable',
    label: 'Tax Portal Unavailable',
    template: 'The {jurisdiction_name} online tax portal is currently unavailable or experiencing technical issues. Will retry later.',
    requiresJurisdiction: true,
  },
  {
    id: 'awaiting_municipal_payoff',
    label: 'Awaiting Municipal Payoff',
    template: 'Waiting for municipal payoff information from {jurisdiction_name}. Requested payoff figures and awaiting response.',
    requiresJurisdiction: true,
  },
  {
    id: 'left_voicemail',
    label: 'Left Voicemail',
    template: 'Left voicemail at {jurisdiction_name}{phone_suffix}. Will follow up if no response within 24-48 hours.',
    requiresJurisdiction: true,
  },
  {
    id: 'fax_sent',
    label: 'Fax Request Sent',
    template: 'Fax request initiated to {jurisdiction_name}{fax_suffix}. Awaiting response.',
    requiresJurisdiction: true,
  },
  {
    id: 'email_sent',
    label: 'Email Request Sent',
    template: 'Email request sent to {jurisdiction_name}{email_suffix}. Awaiting response.',
    requiresJurisdiction: true,
  },
  {
    id: 'custom',
    label: '-- Custom Reason --',
    template: '',
    requiresJurisdiction: false,
  },
];

export const CLARIFICATION_REASON_TEMPLATES = [
  {
    id: 'county_change',
    label: 'County Change',
    template: 'The property county appears to have changed or is different from what was originally provided. Please verify the correct county for this property.',
    requiresJurisdiction: false,
  },
  {
    id: 'multiple_parcels',
    label: 'Multiple Parcels',
    template: 'Multiple parcels were identified for this property. Please confirm if all parcels should be included or specify which parcel(s) to search.',
    requiresJurisdiction: false,
  },
  {
    id: 'unable_to_locate',
    label: 'Unable to Locate Property – Need Parcel or Legal Description',
    template: 'Unable to locate the property with the provided information. Please provide the Parcel Number (APN) or Legal Description to proceed.',
    requiresJurisdiction: false,
  },
  {
    id: 'owner_mismatch',
    label: 'Owner Name Mismatch – Need Parcel or Legal Description',
    template: 'The owner name on record does not match the provided borrower information{owner_suffix}. Please verify and provide the correct Parcel Number (APN) or Legal Description.',
    requiresJurisdiction: false,
  },
  {
    id: 'address_mismatch',
    label: 'Property Address Mismatch',
    template: 'The property address on record does not match the provided address. Please verify the correct property address or provide additional identifying information.',
    requiresJurisdiction: false,
  },
  {
    id: 'parcel_not_found',
    label: 'Parcel Not Found in Records',
    template: 'The provided parcel/APN{parcel_suffix} could not be found in {jurisdiction_name} records. Please verify the parcel number or provide alternative identification.',
    requiresJurisdiction: true,
  },
  {
    id: 'tax_year_clarification',
    label: 'Tax Year Clarification Needed',
    template: 'Need clarification on which tax year(s) should be included in the search{tax_year_suffix}. Please confirm the required tax year coverage.',
    requiresJurisdiction: false,
  },
  {
    id: 'custom',
    label: '-- Custom Reason --',
    template: '',
    requiresJurisdiction: false,
  },
];

/**
 * Replace placeholders in template with actual values
 * Implements smart fallback logic so notes read properly even if some data is missing.
 * 
 * @param {string} template - Template string with placeholders
 * @param {object} data - Object containing replacement values
 * @returns {string} - Template with placeholders replaced
 */
export const applyTemplate = (template, data = {}) => {
  if (!template) return '';
  
  let result = template;
  
  // Extract all available data with normalization
  const jurisdictionName = data.jurisdiction_name || data.jurisdictionName || 
    (data.county ? `${data.county} County` : null);
  const taxOfficeName = data.tax_office_name || data.taxOfficeName || jurisdictionName;
  const phone = data.primary_phone || data.phone_number || data.tax_collector_phone || 
    data.taxCollectorPhone || data.phone;
  const fax = data.fax || data.fax_number || data.taxCollectorFax || data.tax_collector_fax;
  const email = data.email || data.tax_collector_email || data.taxCollectorEmail;
  const website = data.website || data.tax_collector_website || data.taxCollectorWebsite;
  const parcelApn = data.parcel_apn || data.parcelApn || data.apn || data.parcel;
  const ownerName = data.owner_name || data.ownerName || data.borrower_owner_name || data.borrower;
  const taxYear = data.tax_year || data.taxYear;
  const dueDate = data.due_date || data.dueDate;
  const delinquentDate = data.delinquent_date || data.delinquentDate || data.dlq_date;
  const county = data.county;
  
  // Replace jurisdiction_name with smart fallback
  if (jurisdictionName) {
    result = result.replace(/{jurisdiction_name}/g, jurisdictionName);
  } else {
    result = result.replace(/{jurisdiction_name}/g, 'the tax office');
  }
  
  // Replace tax_office_name
  if (taxOfficeName) {
    result = result.replace(/{tax_office_name}/g, taxOfficeName);
  } else {
    result = result.replace(/{tax_office_name}/g, 'the tax office');
  }
  
  // Smart phone suffix (used in templates like "tax office{phone_suffix}")
  if (phone) {
    result = result.replace(/{phone_suffix}/g, ` at ${phone}`);
    result = result.replace(/{primary_phone}/g, phone);
    result = result.replace(/{phone_number}/g, phone);
  } else {
    result = result.replace(/{phone_suffix}/g, '');
    result = result.replace(/ at {primary_phone}/g, '');
    result = result.replace(/ at {phone_number}/g, '');
    result = result.replace(/{primary_phone}/g, '');
    result = result.replace(/{phone_number}/g, '');
  }
  
  // Smart fax suffix — resolves to " at <fax number>" (mirrors phone_suffix behavior).
  // Handles templates where "at" may be glued to the placeholder (e.g. "{jurisdiction_name}at {fax_suffix}").
  if (fax) {
    // First: Normalize "at{fax_suffix}" / "at {fax_suffix}" → " at <number>" (avoid double "at")
    result = result.replace(/\s*at\s*\{fax_suffix\}/gi, ` at ${fax}`);
    // Then: Replace standalone {fax_suffix} → " at <number>" (includes space + "at")
    result = result.replace(/{fax_suffix}/g, ` at ${fax}`);
    result = result.replace(/\s*at\s*\{fax\}/gi, ` at ${fax}`);
    result = result.replace(/{fax}/g, fax);
  } else {
    // Clean up contextual patterns that become orphaned when fax is missing
    // e.g. "sent to {jurisdiction_name}at {fax_suffix} to obtain" → "sent to New Castle County to obtain"
    result = result.replace(/\s*at\s*\{fax_suffix\}/gi, '');
    result = result.replace(/{fax_suffix}/g, '');
    result = result.replace(/\s*at\s*\{fax\}/gi, '');
    result = result.replace(/{fax}/g, '');
  }
  
  // Smart email suffix
  if (email) {
    result = result.replace(/{email_suffix}/g, ` (${email})`);
    result = result.replace(/{email}/g, email);
  } else {
    result = result.replace(/{email_suffix}/g, '');
    result = result.replace(/{email}/g, '');
  }
  
  // Website
  if (website) {
    result = result.replace(/{website}/g, website);
  } else {
    result = result.replace(/{website}/g, '');
  }
  
  // Parcel/APN with smart suffix
  if (parcelApn) {
    result = result.replace(/{parcel_suffix}/g, ` (${parcelApn})`);
    result = result.replace(/{parcel_apn}/g, parcelApn);
  } else {
    result = result.replace(/{parcel_suffix}/g, '');
    result = result.replace(/{parcel_apn}/g, '');
  }
  
  // Owner name with smart suffix
  if (ownerName) {
    result = result.replace(/{owner_suffix}/g, ` (provided: ${ownerName})`);
    result = result.replace(/{owner_name}/g, ownerName);
  } else {
    result = result.replace(/{owner_suffix}/g, '');
    result = result.replace(/{owner_name}/g, '');
  }
  
  // Tax year with smart suffix
  if (taxYear) {
    result = result.replace(/{tax_year_suffix}/g, ` (current: ${taxYear})`);
    result = result.replace(/{tax_year}/g, taxYear);
  } else {
    result = result.replace(/{tax_year_suffix}/g, '');
    result = result.replace(/{tax_year}/g, '');
  }
  
  // Due date
  if (dueDate) {
    result = result.replace(/{due_date}/g, dueDate);
  } else {
    result = result.replace(/{due_date}/g, '');
  }
  
  // Delinquent date
  if (delinquentDate) {
    result = result.replace(/{delinquent_date}/g, delinquentDate);
  } else {
    result = result.replace(/{delinquent_date}/g, '');
  }
  
  // County
  if (county) {
    result = result.replace(/{county}/g, county);
  } else {
    result = result.replace(/{county}/g, '');
  }
  
  // Email with smart suffix (reuses `email` from line 151)
  if (email) {
    result = result.replace(/{email_suffix}/g, ` at ${email}`);
    result = result.replace(/{email}/g, email);
  } else {
    result = result.replace(/{email_suffix}/g, '');
    result = result.replace(/{email}/g, '');
  }

  // Website (reuses `website` from line 152)
  if (website) {
    result = result.replace(/{website}/g, website);
  } else {
    result = result.replace(/{website}/g, '');
  }

  // Fax with smart suffix (reuses `fax` from line 150)
  if (fax) {
    result = result.replace(/\s*at\s*\{fax_suffix\}/gi, ` at ${fax}`);
    result = result.replace(/{fax_suffix}/g, ` at ${fax}`);
    result = result.replace(/\s*at\s*\{fax\}/gi, ` at ${fax}`);
    result = result.replace(/{fax}/g, fax);
  } else {
    result = result.replace(/\s*at\s*\{fax_suffix\}/gi, '');
    result = result.replace(/{fax_suffix}/g, '');
    result = result.replace(/\s*at\s*\{fax\}/gi, '');
    result = result.replace(/{fax}/g, '');
  }

  // Property address
  const propertyAddress = data.property_address || '';
  if (propertyAddress) {
    result = result.replace(/{property_address}/g, propertyAddress);
  } else {
    result = result.replace(/{property_address}/g, '');
  }

  // Client name
  const clientName = data.client_name || '';
  if (clientName) {
    result = result.replace(/{client_name}/g, clientName);
  } else {
    result = result.replace(/{client_name}/g, '');
  }

  // Clean up any remaining unresolved placeholders (omit cleanly)
  result = result.replace(/\{[a-z_]+\}/g, '');
  
  // Clean up any double spaces and trim
  result = result.replace(/\s+/g, ' ').trim();
  
  return result;
};

/**
 * Get templates for a given status
 * @param {string} status - 'pending' or 'clarification'
 * @returns {Array} - Array of template objects
 */
export const getTemplatesForStatus = (status) => {
  if (status === 'pending') {
    return PENDING_REASON_TEMPLATES;
  } else if (status === 'clarification') {
    return CLARIFICATION_REASON_TEMPLATES;
  }
  return [];
};

/**
 * Check if jurisdiction data is available for template resolution
 * @param {object} data - Jurisdiction data object
 * @returns {boolean} - True if meaningful data is available
 */
export const hasJurisdictionData = (data = {}) => {
  return !!(
    data.jurisdiction_name || 
    data.jurisdictionName || 
    data.county || 
    data.tax_collector_phone ||
    data.primary_phone ||
    data.phone_number
  );
};
